// src/utils/invoiceTagging.js
// ===========================================================================
// Re-tag existing invoices so they count toward a job's running balance.
//
// Invoices made before the billing flow, or typed in by hand, have no
// billingKind / contractId, so Job Money and the Contract balance box skip
// them. These helpers let the office:
//   - change an invoice's Invoice # (WO), with a duplicate check that ignores
//     upper/lower case
//   - set its type: standard | deposit | progress | final
//   - say which job it counts toward (by that job's WO)
//
// Used by Approved Invoices (Edit) and Job Money (Add existing invoice).
//
// Sent and Paid invoices only get their tags and numbers updated
// (billingSequence, previouslyBilled, remainingBalance). Their description,
// the wording the customer saw, is never rewritten.
// ===========================================================================

import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  BILLING_KIND,
  getContractIdForJob,
  getJobTotalForBilling,
  getRefreshedHeaderPatch,
  isLedgerInvoice,
  loadContractInvoices,
  parseMoney,
  round2,
} from "./billingFlow";
import { getWOStem } from "./continueJobUtils";

/* ------------------------------------------------------------------------ */
/* Pure helpers                                                             */
/* ------------------------------------------------------------------------ */

export const sameWO = (a, b) =>
  String(a ?? "").trim().toUpperCase() === String(b ?? "").trim().toUpperCase();

/** The spellings worth querying for: as typed, UPPER, lower, Title. */
export const woVariants = (wo) => {
  const v = String(wo ?? "").trim();
  if (!v) return [];
  const lower = v.toLowerCase();
  return Array.from(new Set([v, v.toUpperCase(), lower, lower.charAt(0).toUpperCase() + lower.slice(1)]));
};

const createdMillis = (inv) => {
  const c = inv?.createdAt;
  if (c?.seconds) return c.seconds * 1000;
  const t = new Date(c || inv?.invoiceDate || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

/** Order for a re-tagged invoice: its invoice date, so progress numbers follow the calendar. */
export const orderFromInvoiceDate = (inv) => {
  const t = Date.parse(String(inv?.invoiceDate || "").slice(0, 10));
  if (Number.isFinite(t)) return t;
  return createdMillis(inv) || Date.now();
};

/** The WO shown in "Counts toward job" when an invoice is opened. */
export const currentJobWOForInvoice = (inv = {}) => {
  if (!inv.contractId) return "";
  if (inv.contractJobWO) return String(inv.contractJobWO);
  if (String(inv.contractId).startsWith("wo:")) return String(inv.contractId).slice(3);
  return getWOStem(inv.wo) || String(inv.wo || "");
};

/**
 * Numbers every ledger invoice on one contract should carry, in billing
 * order: progress number, billed before it, balance after it.
 * Returns { [invoiceId]: { billingSequence, previouslyBilled, remainingBalance } }.
 */
export const computeLedgerNumbers = (invoices = []) => {
  const rows = invoices
    .filter(isLedgerInvoice)
    .sort((a, b) => {
      const sa = Number(a.billingOrder ?? 0);
      const sb = Number(b.billingOrder ?? 0);
      if (sa !== sb) return sa - sb;
      return createdMillis(a) - createdMillis(b);
    });

  const out = {};
  let billed = 0;
  let progress = 0;
  rows.forEach((inv) => {
    const amount = round2(parseMoney(inv.amount ?? inv.rate));
    const total = round2(parseMoney(inv.contractTotal));
    const isDeposit = inv.billingKind === BILLING_KIND.DEPOSIT;
    if (inv.billingKind === BILLING_KIND.PROGRESS) progress += 1;
    const before = isDeposit ? 0 : round2(billed);
    out[inv.id] = {
      billingSequence: inv.billingKind === BILLING_KIND.PROGRESS ? progress : 0,
      previouslyBilled: before,
      remainingBalance: round2(total - before - amount),
    };
    billed += amount;
  });
  return out;
};

/* ------------------------------------------------------------------------ */
/* Lookups                                                                  */
/* ------------------------------------------------------------------------ */

const findByWOAnyCase = async (collectionName, field, wo) => {
  const byId = {};
  await Promise.all(
    woVariants(wo).map(async (v) => {
      const snap = await getDocs(query(collection(db, collectionName), where(field, "==", v)));
      snap.docs.forEach((d) => {
        byId[d.id] = { id: d.id, ...d.data() };
      });
    })
  );
  return Object.values(byId);
};

export const findInvoicesByWO = (wo) => findByWOAnyCase("approvedInvoices", "wo", wo);

/** The job with this WO (any case). Exact spelling wins when there are several. */
export const findJobByWO = async (wo) => {
  const jobs = await findByWOAnyCase("jobs", "wo", wo);
  if (!jobs.length) return null;
  const exact = jobs.find((j) => String(j.wo).trim() === String(wo).trim());
  return exact || jobs[0];
};

/**
 * Other invoices and jobs already using this WO, in any case. The invoice's
 * own job does not count as a conflict.
 */
export const findWOConflicts = async ({ wo, invoiceId, ownJobIds = [] }) => {
  if (!String(wo || "").trim()) return { invoices: [], jobs: [] };
  const [invoices, jobs] = await Promise.all([
    findInvoicesByWO(wo),
    findByWOAnyCase("jobs", "wo", wo),
  ]);
  const own = new Set(ownJobIds.filter(Boolean));
  return {
    invoices: invoices.filter((i) => i.id !== invoiceId),
    jobs: jobs.filter((j) => !own.has(j.id)),
  };
};

/** Payments recorded on Deposits under exactly this invoice number. */
export const findPaymentsForInvoiceNumber = async (invoiceNumber) => {
  const key = String(invoiceNumber || "").trim();
  if (!key) return [];
  const snap = await getDocs(query(collection(db, "deposits"), where("invoiceNumber", "==", key)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const movePayments = async (payments, newInvoiceNumber) =>
  Promise.all(
    payments.map((p) =>
      updateDoc(doc(db, "deposits", p.id), {
        invoiceNumber: String(newInvoiceNumber).trim(),
        updatedAt: new Date().toISOString(),
      })
    )
  );

/* ------------------------------------------------------------------------ */
/* Tagging                                                                  */
/* ------------------------------------------------------------------------ */

/**
 * The fields that put an invoice on a job's balance (or take it off).
 *   kind   standard | deposit | progress | final
 *   job    the job object it counts toward, OR
 *   jobWO  that job's WO (looked up, any case); "" = not on any job
 * Returns { patch } or { error }.
 */
export const buildBillingTagPatch = async (invoice, { kind, job, jobWO }) => {
  const k = kind || BILLING_KIND.STANDARD;
  const wantsJob = !!job || !!String(jobWO || "").trim();

  if (!wantsJob) {
    if (k !== BILLING_KIND.STANDARD) {
      return { error: "Pick the job this invoice counts toward (its WO), or set the type to Standard." };
    }
    return {
      patch: { billingKind: k, contractId: null, contractJobWO: "", contractTotal: 0 },
    };
  }

  const target = job || (await findJobByWO(jobWO));
  if (!target) return { error: `No job found with WO ${String(jobWO).trim()}.` };

  const contractId = getContractIdForJob(target);
  const contractTotal = round2(await getJobTotalForBilling(target));
  const sameContract = invoice.contractId === contractId;
  const sameKind = (invoice.billingKind || BILLING_KIND.STANDARD) === k;

  return {
    patch: {
      billingKind: k,
      contractId,
      contractJobWO: String(target.wo || ""),
      contractTotal,
      // Keep its place if nothing about its position changed; otherwise
      // place it by its invoice date.
      billingOrder:
        sameContract && sameKind && invoice.billingOrder ? invoice.billingOrder : orderFromInvoiceDate(invoice),
    },
    job: target,
  };
};

/**
 * Brings every invoice on a contract up to date after a change: open
 * (Draft / Reviewed) invoices get their balance lines rewritten when the
 * text is still ours; Sent and Paid invoices only get their numbers.
 * Returns { [invoiceId]: patch } for what changed.
 */
export const renumberContract = async (contractId) => {
  if (!contractId) return {};
  const list = await loadContractInvoices(contractId);
  const numbers = computeLedgerNumbers(list);
  const patches = {};

  for (const inv of list) {
    if (!isLedgerInvoice(inv)) continue;
    const status = String(inv.status || "Draft").toLowerCase();
    const open = status !== "sent" && status !== "paid";
    const header = open ? getRefreshedHeaderPatch(inv, list) : null;
    const nums = numbers[inv.id];
    const patch = header || nums;
    if (!patch) continue;
    const changed = Object.keys(patch).some((key) => inv[key] !== patch[key]);
    if (!changed) continue;
    try {
      await updateDoc(doc(db, "approvedInvoices", inv.id), { ...patch, updatedAt: new Date().toISOString() });
      patches[inv.id] = patch;
    } catch (err) {
      console.warn("Could not renumber invoice", inv.id, err);
    }
  }
  return patches;
};

/**
 * One-step tag used by Job Money: write the tag, then renumber the contract
 * it left and the one it joined.
 */
export const tagInvoice = async (invoice, options) => {
  const { patch, error } = await buildBillingTagPatch(invoice, options);
  if (error) return { error };
  await updateDoc(doc(db, "approvedInvoices", invoice.id), { ...patch, updatedAt: new Date().toISOString() });
  const touched = new Set([invoice.contractId, patch.contractId].filter(Boolean));
  for (const id of touched) await renumberContract(id);
  return { patch };
};

/* ------------------------------------------------------------------------ */
/* Site lists for the dropdowns                                             */
/* ------------------------------------------------------------------------ */

/** "Oak Ridge  Apts " and "oak ridge apts" are the same site. */
export const normSite = (s) => String(s ?? "").toLowerCase().replace(/\s+/g, " ").trim();

export const invoiceSite = (inv = {}) => inv.siteName || inv.propertyName || "";

/** Natural WO order: PJMC-3041, PJMC-3041-2, PJMC-3041-10, 5456 ... */
export const compareWO = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });

/** Every job at this site (exact site name as stored, then any spacing/case). */
export const loadSiteJobs = async (siteName) => {
  const site = String(siteName || "").trim();
  if (!site) return [];
  const byId = {};
  const variants = Array.from(new Set([site, site.toUpperCase(), site.toLowerCase()]));
  await Promise.all(
    variants.map(async (v) => {
      try {
        const snap = await getDocs(query(collection(db, "jobs"), where("siteName", "==", v)));
        snap.docs.forEach((d) => {
          byId[d.id] = { id: d.id, ...d.data() };
        });
      } catch (err) {
        console.warn("Site jobs lookup failed:", err);
      }
    })
  );
  return Object.values(byId)
    .filter((j) => String(j.wo ?? "").trim() && String(j.status || "").toLowerCase() !== "deleted")
    .sort((a, b) => compareWO(a.wo, b.wo));
};

/** Every approved invoice at this site. */
export const loadSiteInvoices = async (siteName) => {
  const site = String(siteName || "").trim();
  if (!site) return [];
  const byId = {};
  const variants = Array.from(new Set([site, site.toUpperCase(), site.toLowerCase()]));
  await Promise.all(
    variants.flatMap((v) =>
      ["siteName", "propertyName"].map(async (field) => {
        try {
          const snap = await getDocs(query(collection(db, "approvedInvoices"), where(field, "==", v)));
          snap.docs.forEach((d) => {
            byId[d.id] = { id: d.id, ...d.data() };
          });
        } catch (err) {
          console.warn("Site invoices lookup failed:", err);
        }
      })
    )
  );
  return Object.values(byId).sort((a, b) => compareWO(a.wo, b.wo));
};

/** "PJMC-3041-2 · 09/15 · $2,000.00 · Sent · Progress 1" for dropdowns. */
export const invoiceOptionLabel = (inv = {}, money = (n) => `$${Number(n || 0).toFixed(2)}`) => {
  const type =
    inv.billingKind === BILLING_KIND.PROGRESS
      ? `Progress ${inv.billingSequence || ""}`.trim()
      : inv.billingKind && inv.billingKind !== BILLING_KIND.STANDARD
      ? inv.billingKind.charAt(0).toUpperCase() + inv.billingKind.slice(1)
      : "Standard";
  const date = String(inv.invoiceDate || "").slice(5, 10).replace("-", "/");
  return [inv.wo || "(no #)", date, money(inv.amount ?? inv.rate), inv.status || "Draft", type]
    .filter(Boolean)
    .join(" · ");
};
